import React from 'react';
import { cn } from '@/lib/utils';

interface MessageContentProps {
  content: string;
  isAssistant?: boolean;
}

// Enhanced markdown renderer that supports proper multi-line formatting
const MessageContent: React.FC<MessageContentProps> = ({ content, isAssistant = true }) => {
  const renderMarkdown = () => {
    if (!content) return null;
    
    // Process the entire content as a whole to handle multi-line features
    let processedContent = content;
    const elements: React.ReactNode[] = [];
    let currentIndex = 0;
    
    // Handle multi-line code blocks first
    const codeBlockRegex = /```[\s\S]*?```/g;
    let codeBlockMatch;
    const codeBlocks: { start: number; end: number; content: string; language?: string }[] = [];
    
    while ((codeBlockMatch = codeBlockRegex.exec(content)) !== null) {
      const fullMatch = codeBlockMatch[0];
      const lines = fullMatch.split('\n');
      const firstLine = lines[0];
      const language = firstLine.length > 3 ? firstLine.substring(3).trim() : '';
      const codeContent = lines.slice(1, -1).join('\n');
      
      codeBlocks.push({
        start: codeBlockMatch.index,
        end: codeBlockMatch.index + fullMatch.length,
        content: codeContent,
        language
      });
    }
    
    // Split content into segments, handling code blocks
    const segments: { type: 'text' | 'codeblock'; content: string; language?: string }[] = [];
    let lastEnd = 0;
    
    codeBlocks.forEach((block) => {
      // Add text before code block
      if (block.start > lastEnd) {
        segments.push({
          type: 'text',
          content: content.substring(lastEnd, block.start)
        });
      }
      
      // Add code block
      segments.push({
        type: 'codeblock',
        content: block.content,
        language: block.language
      });
      
      lastEnd = block.end;
    });
    
    // Add remaining text
    if (lastEnd < content.length) {
      segments.push({
        type: 'text',
        content: content.substring(lastEnd)
      });
    }
    
    // If no code blocks, treat entire content as text
    if (segments.length === 0) {
      segments.push({ type: 'text', content });
    }
    
    // Process each segment
    return segments.map((segment, segmentIndex) => {
      if (segment.type === 'codeblock') {
        return (
          <div 
            key={`codeblock-${segmentIndex}`}
            className="bg-slate-100 dark:bg-slate-800/80 rounded-lg p-3 my-3 font-mono text-sm overflow-auto border border-slate-200 dark:border-slate-700"
          >
            {segment.language && (
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-2 font-sans">
                {segment.language}
              </div>
            )}
            <pre className="whitespace-pre-wrap">{segment.content}</pre>
          </div>
        );
      }
      
      // Process text content line by line for other markdown features
      const lines = segment.content.split('\n');
      const textElements: React.ReactNode[] = [];
      
      lines.forEach((line, lineIndex) => {
        const key = `${segmentIndex}-${lineIndex}`;
        
        // Empty lines become line breaks
        if (line.trim() === '') {
          textElements.push(<br key={key} />);
          return;
        }
        
        // Handle headings - convert to bold for chat feel
        if (line.startsWith('# ')) {
          textElements.push(
            <p key={key} className="my-2">
              <strong className="text-lg">{line.substring(2)}</strong>
            </p>
          );
          return;
        }
        if (line.startsWith('## ')) {
          textElements.push(
            <p key={key} className="my-2">
              <strong className="text-base">{line.substring(3)}</strong>
            </p>
          );
          return;
        }
        if (line.startsWith('### ')) {
          textElements.push(
            <p key={key} className="my-2">
              <strong>{line.substring(4)}</strong>
            </p>
          );
          return;
        }
        
        // Handle list items
        if (line.match(/^[\s]*[-*+]\s/)) {
          const indent = line.match(/^(\s*)/)?.[1]?.length || 0;
          const content = line.replace(/^[\s]*[-*+]\s/, '');
          textElements.push(
            <div key={key} className="my-1" style={{ marginLeft: `${indent * 12}px` }}>
              <span className="mr-2">•</span>
              {processInlineMarkdown(content, key)}
            </div>
          );
          return;
        }
        
        // Handle numbered lists
        if (line.match(/^[\s]*\d+\.\s/)) {
          const indent = line.match(/^(\s*)/)?.[1]?.length || 0;
          const match = line.match(/^[\s]*(\d+)\.\s(.*)$/);
          if (match) {
            const [, number, content] = match;
            textElements.push(
              <div key={key} className="my-1" style={{ marginLeft: `${indent * 12}px` }}>
                <span className="mr-2">{number}.</span>
                {processInlineMarkdown(content, key)}
              </div>
            );
            return;
          }
        }
        
        // Regular paragraph with inline markdown
        textElements.push(
          <p key={key} className="my-1.5">
            {processInlineMarkdown(line, key)}
          </p>
        );
      });
      
      return <div key={`segment-${segmentIndex}`}>{textElements}</div>;
    });
  };
  
  // Helper function to process inline markdown (bold, italic, code, links)
  const processInlineMarkdown = (text: string, baseKey: string): React.ReactNode[] => {
    const elements: React.ReactNode[] = [];
    let remaining = text;
    let elementIndex = 0;
    
    while (remaining.length > 0) {
      // Find the next markdown element
      const patterns = [
        { regex: /\*\*([^*]+)\*\*/, type: 'bold' },
        { regex: /\*([^*]+)\*/, type: 'italic' },
        { regex: /`([^`]+)`/, type: 'code' },
        { regex: /\[([^\]]+)\]\(([^)]+)\)/, type: 'link' }
      ];
      
      let earliestMatch: { index: number; length: number; type: string; content: string; url?: string } | null = null;
      
      patterns.forEach(pattern => {
        const match = remaining.match(pattern.regex);
        if (match && (earliestMatch === null || match.index! < earliestMatch.index)) {
          earliestMatch = {
            index: match.index!,
            length: match[0].length,
            type: pattern.type,
            content: match[1],
            url: match[2] // for links
          };
        }
      });
      
      if (earliestMatch) {
        // Add text before the match
        if (earliestMatch.index > 0) {
          elements.push(
            <span key={`${baseKey}-text-${elementIndex++}`}>
              {remaining.substring(0, earliestMatch.index)}
            </span>
          );
        }
        
        // Add the formatted element
        const key = `${baseKey}-${earliestMatch.type}-${elementIndex++}`;
        switch (earliestMatch.type) {
          case 'bold':
            elements.push(<strong key={key}>{earliestMatch.content}</strong>);
            break;
          case 'italic':
            elements.push(<em key={key}>{earliestMatch.content}</em>);
            break;
          case 'code':
            elements.push(
              <code 
                key={key}
                className="bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded font-mono text-sm"
              >
                {earliestMatch.content}
              </code>
            );
            break;
          case 'link':
            elements.push(
              <a 
                key={key}
                href={earliestMatch.url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "underline hover:no-underline",
                  isAssistant ? "text-blue-600 dark:text-blue-400" : "text-blue-100"
                )}
              >
                {earliestMatch.content}
              </a>
            );
            break;
        }
        
        // Continue with remaining text
        remaining = remaining.substring(earliestMatch.index + earliestMatch.length);
      } else {
        // No more markdown, add remaining text
        if (remaining) {
          elements.push(
            <span key={`${baseKey}-text-${elementIndex++}`}>
              {remaining}
            </span>
          );
        }
        break;
      }
    }
    
    return elements.length > 0 ? elements : [text];
  };

  return (
    <div 
      className={cn(
        "prose-sm max-w-none break-words",
        !isAssistant && "text-slate-900 dark:text-blue-100"
      )}
      data-testid="message-content"
    >
      {renderMarkdown()}
    </div>
  );
};

export default MessageContent;
